import { useMemo } from "react"
import { useFormContext, useWatch } from "react-hook-form"
import { useTranslation } from "react-i18next"
import { usePrevious } from "@uidotdev/usehooks"
import { addMonths } from "date-fns"
import { DetailCheckbox } from "@/components/ui/checkbox"
import { DatePicker } from "@/components/ui/date-picker"
import { FormField } from "@/components/ui/form"
import { Input, TextArea } from "@/components/ui/input"
import { RadioGroup, RadioGroupItem, RadioGroupLabel } from "@/components/ui/radio-group"
import {
  LabelAndSelectValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import { DateOnly } from "@/lib/date-only"
import { DOT_CHARACTER } from "@/lib/format-date"
import { cn } from "@/lib/utils"
import { textAreaDefaultMaxLength } from "@/models/common"
import { PreparationSelection } from "@/modules/patients/detail/medication/de/preparation-selection"
import {
  Dosage,
  MedicationSchemaType,
  medicationUsageInstructionMaxLength,
} from "@/modules/patients/detail/medication/de/schema/medication-schema"
import { Medication } from "@/modules/patients/detail/medication/de/search-results"
import { SubstanceSelection } from "@/modules/patients/detail/medication/de/substance-selection"
import { useMedications } from "@/modules/patients/detail/medication/hooks/useMedications"
import { validateDosageFormat } from "@/modules/patients/detail/medication/lib/validate-dosage-format"
import { useDeMedicationContext } from "@/modules/patients/detail/medication/store/useDeMedicationStore"
import { DeMedicationFragmentInlineList } from "@/modules/patients/detail/medication/types/medication-fragment-type"
import { UNITS } from "@/modules/patients/detail/medication/types/medication-unit"
import { ArrElement } from "@/types/ArrElement"

const formatPharmaInformationText = (
  pharma: ArrElement<Medication["pharmaceuticalCentralNumberList"]> | undefined,
): string => {
  if (!pharma) return ""
  return `${pharma.unit} (${pharma.quantity}) ${pharma.price}`
}

interface MedicationCreationProps {
  selectedPharma?: ArrElement<Medication["pharmaceuticalCentralNumberList"]>
  pharmaceuticalCentralNumberList: Medication["pharmaceuticalCentralNumberList"] | undefined
  isAddingFreeTextMedication: boolean
  onPharmaChange: (pharma: ArrElement<Medication["pharmaceuticalCentralNumberList"]>) => void
}

const MedicationCreation = (props: MedicationCreationProps) => {
  const { isAddingFreeTextMedication, selectedPharma, pharmaceuticalCentralNumberList, onPharmaChange } = props
  const { t } = useTranslation()
  const { activeMedications } = useMedications<DeMedicationFragmentInlineList>()
  const {
    register,
    setValue,
    getValues,
    trigger,
    control,
    formState: { errors },
  } = useFormContext<MedicationSchemaType>()

  const { medication, substance, startDate, endDate } = getValues()
  const asNeeded = useWatch({ control, name: "asNeeded" })
  const isPermanentPrescription = useWatch({ control, name: "isPermanentPrescription" })
  const unitWatch = useWatch({ control, name: "unit" })
  const previousUnit = usePrevious(unitWatch)

  const formattedPharmaceuticalCentralNumberList = useMemo(
    () =>
      activeMedications
        .flatMap((item) => item.pharmaceuticalCentralNumber ?? "")
        .filter((pharmaceuticalCentralNumber) => pharmaceuticalCentralNumber),
    [activeMedications],
  )
  const pharmaceuticalCentralNumbers = useDeMedicationContext((state) => state.pharmaceuticalCentralNumberList)
  const setPharmaceuticalCentralNumberList = useDeMedicationContext((state) => state.setPharmaceuticalCentralNumberList)

  const handleDosageInputChange = (field: Dosage, value: string) => {
    if (!validateDosageFormat(value)) {
      return
    }
    setValue(field, value, {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  const formatDosage = (field: Dosage, value: string) => {
    if (!value.endsWith(DOT_CHARACTER)) return
    setValue(field, value.replace(DOT_CHARACTER, ""), {
      shouldValidate: true,
      shouldDirty: true,
    })
  }

  return (
    <div className="py-4">
      {medication && <PreparationSelection medication={medication} selectedPharma={selectedPharma} />}
      {substance && <SubstanceSelection substance={substance} />}

      <div className="flex flex-col gap-4">
        {isAddingFreeTextMedication && (
          <Input
            {...register("freeTextMedicationName")}
            data-testid="free-text-medication-name-input"
            error={errors.freeTextMedicationName?.message}
            translateError={true}
            label={t("patient.prescription.freeTextMedicationName")}
            required
          />
        )}
        <FormField
          control={control}
          name="isPermanentPrescription"
          render={({ field: { onChange, value } }) => {
            return (
              <DetailCheckbox
                checked={value}
                onCheckedChange={(ticked) => {
                  onChange(ticked)
                  setValue("asNeeded", false)

                  if (ticked && getValues("unit") === "PRN") {
                    setValue("unit", "PC")
                  }

                  if (!ticked) {
                    return
                  }

                  if (!startDate) {
                    setValue("startDate", new Date())
                  }

                  if (!endDate) {
                    const endDate = addMonths(new Date(), 6)
                    setValue("endDate", endDate)
                  }
                }}
                className="w-fit">
                {t("patient.medication.permanentPrescription")}
              </DetailCheckbox>
            )
          }}
        />
        <div className="grid w-full grid-cols-2 gap-4">
          <DatePicker
            data-testid="start-date-input"
            className="col-span-1"
            label={t("patient.medication.startDate")}
            onSelect={(date) => {
              if (date) {
                setValue("startDate", date, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              trigger("endDate")
            }}
            onReset={() =>
              setValue("startDate", undefined, {
                shouldDirty: true,
              })
            }
            error={errors.startDate?.message}
            translateError
            errorClasses="truncate"
            value={startDate}
          />
          <DatePicker
            disabledMatcher={(d) => {
              return startDate ? new DateOnly(d).isLowerThan(new DateOnly(startDate)) : false
            }}
            data-testid="end-date-input"
            className="col-span-1"
            label={t("patient.medication.endDate")}
            onSelect={(date) => {
              if (date) {
                setValue("endDate", date, {
                  shouldValidate: true,
                  shouldDirty: true,
                })
              }
              trigger("startDate")
            }}
            onReset={() =>
              setValue("endDate", undefined, {
                shouldDirty: true,
              })
            }
            error={errors.endDate?.message}
            translateError
            errorClasses="truncate"
            value={endDate}
          />
        </div>
        <FormField
          control={control}
          name="asNeeded"
          render={({ field: { onChange, value } }) => {
            return (
              <DetailCheckbox
                disabled={isPermanentPrescription}
                checked={value}
                onCheckedChange={(e) => {
                  onChange(e)
                  if (e) setValue("unit", "PRN")
                  else {
                    if (previousUnit) setValue("unit", previousUnit)
                    else setValue("unit", "PC")
                  }
                }}
                className="w-fit">
                {t("patient.medication.unitList.PRN")}
              </DetailCheckbox>
            )
          }}
        />
        {!asNeeded && (
          <div className="grid gap-2">
            <label className="text-on-surface-subdued text-sm">{t("patient.medication.unit")}</label>
            <FormField
              name="unit"
              render={({ field: { value, onChange } }) => {
                return (
                  <RadioGroup
                    value={value}
                    className="flex flex-wrap"
                    data-testid="unit-radio-group"
                    onValueChange={onChange}>
                    {UNITS.map((unit) => (
                      <RadioGroupItem className="p-1" value={unit} key={unit} data-testid={`${unit}-radio-item`}>
                        <RadioGroupLabel>{t(`patient.medication.unitList.${unit}`)}</RadioGroupLabel>
                      </RadioGroupItem>
                    ))}
                  </RadioGroup>
                )
              }}
            />
          </div>
        )}
        {!asNeeded && (
          <div className="grid w-full grid-cols-4 gap-4">
            <FormField
              name="morningDosage"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <Input
                    {...register("morningDosage")}
                    label={t("patient.medication.morning")}
                    wrapperProps={{ className: "col-span-1" }}
                    error={errors.morningDosage?.message}
                    translateError
                    errorClasses="truncate"
                    required
                    onChange={(e) => handleDosageInputChange("morningDosage", e.target.value)}
                    onBlur={(e) => formatDosage("morningDosage", e.target.value)}
                    data-testid="morning-dosage-input"
                    value={value}
                  />
                )
              }}
            />
            <FormField
              name="noonDosage"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <Input
                    {...register("noonDosage")}
                    label={t("patient.medication.noon")}
                    wrapperProps={{ className: "col-span-1" }}
                    error={errors.noonDosage?.message}
                    translateError
                    errorClasses="truncate"
                    required
                    onChange={(e) => handleDosageInputChange("noonDosage", e.target.value)}
                    onBlur={(e) => formatDosage("noonDosage", e.target.value)}
                    value={value}
                    data-testid="noon-dosage-input"
                  />
                )
              }}
            />
            <FormField
              name="eveningDosage"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <Input
                    {...register("eveningDosage")}
                    label={t("patient.medication.evening")}
                    wrapperProps={{ className: "col-span-1" }}
                    error={errors.eveningDosage?.message}
                    translateError
                    errorClasses="truncate"
                    required
                    onChange={(e) => handleDosageInputChange("eveningDosage", e.target.value)}
                    onBlur={(e) => formatDosage("eveningDosage", e.target.value)}
                    data-testid="evening-dosage-input"
                    value={value}
                  />
                )
              }}
            />
            <FormField
              name="nightDosage"
              control={control}
              render={({ field: { value } }) => {
                return (
                  <Input
                    {...register("nightDosage")}
                    label={t("patient.medication.night")}
                    wrapperProps={{ className: "col-span-1" }}
                    error={errors.nightDosage?.message}
                    translateError
                    errorClasses="truncate"
                    required
                    onChange={(e) => handleDosageInputChange("nightDosage", e.target.value)}
                    onBlur={(e) => formatDosage("nightDosage", e.target.value)}
                    data-testid="night-dosage-input"
                    value={value}
                  />
                )
              }}
            />
          </div>
        )}
        <TextArea
          {...register("usageInstruction")}
          label={t("patient.medication.usageInstructions")}
          error={errors.usageInstruction?.message}
          data-testid="usage-instructions-input"
          translateError
          textLimit={medicationUsageInstructionMaxLength}
        />
        <div className="pt-3">
          <TextArea
            {...register("internalNote")}
            label={t("patient.medication.internalNotes")}
            error={errors.internalNote?.message}
            data-testid="internal-notes-input"
            translateError
            textLimit={textAreaDefaultMaxLength}
          />
        </div>
        <div className="grid gap-4">
          <p className="text-on-surface text-base font-medium">
            {t("patient.medication.forPrescriptionSection.forPrescriptions")}
          </p>
          <div className="grid grid-cols-2 gap-4">
            <Select
              value={selectedPharma?.pharmaceuticalCentralNumber}
              onValueChange={(value: string) => {
                const foundPharma = pharmaceuticalCentralNumberList?.find(
                  (item) => item.pharmaceuticalCentralNumber === value,
                )
                if (foundPharma) {
                  if (selectedPharma?.pharmaceuticalCentralNumber) {
                    const pharmaceuticalCentralNumberIndex = pharmaceuticalCentralNumbers.indexOf(
                      selectedPharma?.pharmaceuticalCentralNumber,
                    )
                    if (pharmaceuticalCentralNumberIndex > -1)
                      formattedPharmaceuticalCentralNumberList.splice(pharmaceuticalCentralNumberIndex, 1)
                    setPharmaceuticalCentralNumberList([
                      ...formattedPharmaceuticalCentralNumberList,
                      foundPharma.pharmaceuticalCentralNumber,
                    ])
                  }
                  onPharmaChange(foundPharma)
                  setValue("pharmaceuticalCentralNumber", foundPharma.pharmaceuticalCentralNumber, {
                    shouldDirty: true,
                  })
                }
              }}
              data-testid="pharma-code-list">
              <SelectTrigger className="col-span-1">
                <SelectValue placeholder={t("patient.medication.forPrescriptionSection.packingSizeAndPrice")}>
                  <LabelAndSelectValue
                    className={cn(
                      "relative top-0 left-0 whitespace-nowrap transition-none",
                      selectedPharma && "absolute top-1 left-4",
                    )}
                    label={t("patient.medication.forPrescriptionSection.packingSizeAndPrice")}
                    value={formatPharmaInformationText(selectedPharma)}
                  />
                </SelectValue>
              </SelectTrigger>
              <SelectContent>
                {pharmaceuticalCentralNumberList && pharmaceuticalCentralNumberList.length > 0 ? (
                  pharmaceuticalCentralNumberList.map((pharma) => {
                    const name = formatPharmaInformationText(pharma)
                    return (
                      <SelectItem
                        key={pharma.pharmaceuticalCentralNumber}
                        title={name}
                        value={pharma.pharmaceuticalCentralNumber}>
                        {name}
                      </SelectItem>
                    )
                  })
                ) : (
                  <SelectItem value="-1" disabled>
                    {t("patient.medication.forPrescriptionSection.nothingToSelect")}
                  </SelectItem>
                )}
              </SelectContent>
            </Select>
            <FormField
              name="numberOfPackages"
              control={control}
              render={({ field: { value, onChange } }) => {
                return (
                  <Select
                    value={`${value}`}
                    onValueChange={(e: string) => onChange(Number(e))}
                    data-testid="number-of-packages">
                    <SelectTrigger className="col-span-1">
                      <SelectValue placeholder={t("patient.medication.forPrescriptionSection.packingSizeAndPrice")}>
                        <LabelAndSelectValue
                          className={cn(
                            "relative top-0 left-0 whitespace-nowrap transition-none",
                            value && "absolute top-1 left-4 truncate",
                          )}
                          label="Number of packages"
                          value={`${value}`}
                        />
                      </SelectValue>
                    </SelectTrigger>
                    <SelectContent>
                      {Array.from({ length: 9 }, (_, i) => (
                        <SelectItem key={i + 1} value={`${i + 1}`}>
                          {i + 1}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )
              }}
            />
          </div>
          <FormField
            name="refuseSubstitutes"
            control={control}
            render={({ field: { value, onChange } }) => {
              return (
                <DetailCheckbox
                  className="bg-surface! hover:bg-surface! w-full flex-row-reverse justify-between border-none p-0 [&>svg]:mt-3"
                  onCheckedChange={onChange}
                  checked={value}
                  testId="refuse-substitutes-checkbox"
                  childClassName="p-0">
                  <div>
                    <p className="text-on-surface text-sm font-normal">
                      {t("patient.medication.forPrescriptionSection.refuseSubstitutes")}
                    </p>
                    <p className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
                      {t("patient.medication.forPrescriptionSection.description")}
                    </p>
                  </div>
                </DetailCheckbox>
              )
            }}
          />
        </div>
      </div>
    </div>
  )
}

export { MedicationCreation }
